/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useState, type CSSProperties, type MouseEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DragHandleDots2Icon } from '@radix-ui/react-icons';
import { UnstyledButton } from '@/shared/ui';

interface SortableNavRowProps {
  id: string;
  /**
   * Whether the drag handle renders at all. There's no room for it in the
   * collapsed (icon-only) rail, so reordering is expanded-only.
   */
  showHandle: boolean;
  /**
   * How the handle occupies the row.
   *
   * - `inline` (default): laid out as a flex sibling, always visible. Right
   *   for rows that already end in their own control, which an overlay would
   *   cover.
   * - `hover-overlay`: absolutely positioned over the row's right edge and
   *   revealed only on hover (or keyboard focus, or while dragging). Right
   *   for the sidebar, where reserving a column made the active row's tinted
   *   background stop short of the rail's edge.
   */
  handleMode?: 'inline' | 'hover-overlay';
  /**
   * Height in px of the row the handle belongs to, when the wrapped element is
   * taller than one row. An open nav group wraps its children too, so without
   * this the handle centres itself over the middle child and reads as that
   * child's handle; hovering a child would reveal it as well.
   */
  handleRowHeight?: number;
  children: ReactNode;
}

/**
 * Wraps a single sidebar row (link or expandable group) with a dedicated
 * drag handle, so long-pressing the handle reorders the row while pressing
 * anywhere else on it — its label, its icon — always navigates immediately.
 * Sharing one element for both gestures is what caused the earlier bug: a
 * long-press that activated the drag sensor still ended in a native click on
 * the link underneath, firing an unwanted navigation. A separate handle has
 * no click semantics of its own, so that race can't happen.
 */
export function SortableNavRow({
  id,
  showHandle,
  handleMode = 'inline',
  handleRowHeight,
  children,
}: SortableNavRowProps) {
  const { t } = useTranslation('@pages/layout');
  const [revealed, setRevealed] = useState(false);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: !showHandle,
  });

  const rowStyle: CSSProperties = {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    outline: isDragging ? '2px solid var(--seed-base)' : 'none',
    borderRadius: 'var(--radius-md)',
  };

  const overlay = handleMode === 'hover-overlay';
  const handleVisible = !overlay || revealed || isDragging;

  /*
   * With a row height given, the pointer only counts as "on this row" while it
   * is within that band, so an open group's handle answers to its header alone
   * and not to the children stacked underneath it.
   */
  const trackPointer = (e: MouseEvent<HTMLDivElement>) => {
    if (!handleRowHeight) return setRevealed(true);
    const top = e.currentTarget.getBoundingClientRect().top;
    setRevealed(e.clientY - top <= handleRowHeight);
  };

  return (
    <div
      ref={setNodeRef}
      style={rowStyle}
      onMouseEnter={trackPointer}
      onMouseMove={trackPointer}
      onMouseLeave={() => setRevealed(false)}
    >
      <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
      {showHandle && (
        <UnstyledButton
          aria-label={t('menu.reorder')}
          {...attributes}
          {...listeners}
          onFocus={() => setRevealed(true)}
          onBlur={() => setRevealed(false)}
          style={{
            ...(overlay
              ? handleRowHeight
                ? { position: 'absolute' as const, right: 2, top: (handleRowHeight - 40) / 2 }
                : { position: 'absolute' as const, right: 2, top: 0, bottom: 0, margin: 'auto 0' }
              : { flexShrink: 0 }),
            width: 24,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'grab',
            touchAction: 'none',
            color: 'var(--text-tertiary)',
            opacity: handleVisible ? 1 : 0,
            pointerEvents: handleVisible ? 'auto' : 'none',
            transition: 'opacity 120ms ease',
          }}
        >
          <DragHandleDots2Icon width={14} height={14} />
        </UnstyledButton>
      )}
    </div>
  );
}
